import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { StringField } from "@/components/shared/FormFields";
import SpeakerButton from "@/components/ui/SpeakerButton";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import useSpeechRecognition from "@/hooks/useSpeechRecognition";
import useExerciseEngine from "@/hooks/useExerciseEngine";
import { cn } from "@/utils/utils";

/* =====================================================
   PRONUNCIATION EXERCISE (ENGINE INTEGRATED)
===================================================== */

export default function PronunciationExercise({
  exercise = {},
  userAnswer = {},
  onUpdate,
  isPreview = false,
  showResults = false,
  onComplete = () => {},
  setAttempt = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= QUESTIONS ================= */
  const questions = Array.isArray(exercise.pronunciationQuestions)
    ? exercise.pronunciationQuestions.map((q) => ({
        type: "speech",
        textToPronounce: q.textToPronounce?.trim() || "",
        answer: q.textToPronounce?.trim() || "",
        ipaSound: q.ipaSound?.trim() || "",
        requireCapitalization: !!q.requireCapitalization,
        skill: q.skill || "pronunciation",
      }))
    : [];

  /* ================= ENGINE ================= */
  const {
    answers,
    setAnswer,
    submitIfReady,
  } = useExerciseEngine({
    questions,
    showResult: showResults,
    onComplete,
    externalAnswers: userAnswer || {},
  });

  /* ================= SPEECH ================= */
  const recognition = useSpeechRecognition() || {};

  const {
    transcript = "",
    listening = false,
    startListening = () => {},
    stopListening = () => {},
    resetTranscript = () => {},
  } = recognition;

  /* ================= STATE ================= */
  const [recordingIndex, setRecordingIndex] = useState(null);
  const [liveTranscript, setLiveTranscript] = useState("");
  const activeIndexRef = useRef(null);

 
  /* ================= START ================= */
  const handleStartListening = (idx) => {
    if (listening) return;

    setLiveTranscript("");
    activeIndexRef.current = idx;
    setRecordingIndex(idx);
    resetTranscript();

    startListening({
      continuous: false,
      interimResults: true,
      lang: "en-GB",
    });
  };

  /* ================= STOP ================= */
  const handleStopListening = () => {
    stopListening();
    setRecordingIndex(null);
  };

  /* ================= HANDLE RESULT ================= */
  useEffect(() => {
    if (!transcript) return;

    if (listening) {
      setLiveTranscript(transcript);
      return;
    }

    const idx = activeIndexRef.current;
    if (idx === null || !questions[idx]) return;

    const spokenValue = liveTranscript || transcript;

    // ✅ ENGINE HANDLE
    setAnswer(idx, spokenValue);
    submitIfReady();

    // save attempt
    setAttempt({
      exerciseId: exercise._id,
      answers: {
        ...answers,
        [idx]: spokenValue,
      },
    });

    activeIndexRef.current = null;
    setLiveTranscript("");
    resetTranscript();
  }, [transcript, listening]);

  /* ================= EDIT MODE ================= */
  if (isEditMode) {
    const updateQuestions = (next) =>
      onUpdate("pronunciationQuestions", next);

    const update = (idx, field, value) => {
      const next = [...questions];
      next[idx] = { ...next[idx], [field]: value };
      updateQuestions(next);
    };

    return (
      <div className="space-y-8">
        {questions.map((q, idx) => (
          <Card key={idx} className="p-6 space-y-4">
            <StringField
              label={`Text ${idx + 1}`}
              value={q.textToPronounce}
              onChange={(v) => update(idx, "textToPronounce", v)}
              required
            />

            <SpeakerButton text={q.textToPronounce} />

            <StringField
              label="IPA"
              value={q.ipaSound}
              onChange={(v) => update(idx, "ipaSound", v)}
            />

            <CapitalizationRule
              checked={q.requireCapitalization}
              onChange={(c) =>
                update(idx, "requireCapitalization", c)
              }
            />
          </Card>
        ))}
      </div>
    );
  }

  /* ================= EMPTY ================= */
  if (!questions.length) {
    return (
      <Card>
        <CardHeader>
          <CardDescription>No questions available</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  /* ================= STUDENT UI ================= */
  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle>Pronunciation Practice</CardTitle>
        <CardDescription>Speak clearly</CardDescription>
      </CardHeader>

      <CardContent className="space-y-10">
        {questions.map((q, idx) => {
          const spoken = answers[idx];

          return (
            <div
              key={idx}
              className="border rounded-xl p-6 text-center space-y-4"
            >
              <div className="text-2xl font-bold text-blue-700">
                {q.textToPronounce}
              </div>

              <SpeakerButton text={q.textToPronounce} />

              {/* BUTTONS */}
              <div className="flex justify-center gap-3">
                {recordingIndex !== idx ? (
                  <Button
                    onClick={() => handleStartListening(idx)}
                    disabled={showResults}
                  >
                    🎤 Speak
                  </Button>
                ) : (
                  <>
                    <Button disabled className="animate-pulse">
                      Recording...
                    </Button>
                    <Button onClick={handleStopListening}>
                      Stop
                    </Button>
                  </>
                )}
              </div>

              {/* TRANSCRIPT */}
              {(recordingIndex === idx || spoken) && (
                <div className="p-3 border rounded bg-muted">
                  {recordingIndex === idx
                    ? liveTranscript || "Listening..."
                    : spoken}
                </div>
              )}

              {/* RESULT */}
              {showResults && spoken && (
                <div className="text-sm font-medium">
                  {spoken === q.answer ? (
                    <span className="text-green-600">Correct</span>
                  ) : (
                    <span className="text-red-600">Wrong</span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}